import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, ArrowUpDownIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, FileIcon } from 'lucide-react';
import { BulkPaymentBatch } from '../pages/BulkPayments';
interface BulkPaymentListProps {
  onViewDetail: (batchId: string) => void;
}
export function BulkPaymentList({
  onViewDetail
}: BulkPaymentListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'createdAt' | 'name' | 'totalAmount' | 'collectionRate'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Mock batch data
  const batches: BulkPaymentBatch[] = [{
    id: '1',
    name: 'December Invoices',
    createdAt: '2023-12-15T09:30:00',
    totalAmount: 12450.75,
    currency: 'USD',
    totalItems: 45,
    completedItems: 38,
    status: 'completed',
    collectionRate: 84.4
  }, {
    id: '2',
    name: 'Q1 Subscriptions',
    createdAt: '2023-12-01T14:15:00',
    totalAmount: 8750.0,
    currency: 'USD',
    totalItems: 25,
    completedItems: 23,
    status: 'completed',
    collectionRate: 92.0
  }, {
    id: '3',
    name: 'Event Tickets - Summer Festival',
    createdAt: '2023-11-20T11:45:00',
    totalAmount: 4500.0,
    currency: 'USD',
    totalItems: 150,
    completedItems: 127,
    status: 'completed',
    collectionRate: 84.7
  }, {
    id: '4',
    name: 'Membership Renewals',
    createdAt: '2023-12-18T16:20:00',
    totalAmount: 3600.0,
    currency: 'USD',
    totalItems: 12,
    completedItems: 5,
    status: 'processing',
    collectionRate: 41.7
  }, {
    id: '5',
    name: 'Workshop Registrations',
    createdAt: '2023-12-10T08:30:00',
    totalAmount: 2250.0,
    currency: 'USD',
    totalItems: 15,
    completedItems: 15,
    status: 'completed',
    collectionRate: 100.0
  }];
  // Sort and filter batches
  const filteredBatches = batches.filter(batch => batch.name.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => {
    if (sortField === 'createdAt') {
      return sortDirection === 'asc' ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    if (sortField === 'totalAmount') {
      return sortDirection === 'asc' ? a.totalAmount - b.totalAmount : b.totalAmount - a.totalAmount;
    }
    if (sortField === 'collectionRate') {
      return sortDirection === 'asc' ? a.collectionRate - b.collectionRate : b.collectionRate - a.collectionRate;
    }
    // Default to name sort
    return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
  });
  const handleSort = (field: 'createdAt' | 'name' | 'totalAmount' | 'collectionRate') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-223" />;
      case 'processing':
        return <ClockIcon size={16} className="text-blue-500" data-id="element-224" />;
      case 'failed':
        return <AlertCircleIcon size={16} className="text-red-500" data-id="element-225" />;
      default:
        return <FileIcon size={16} className="text-gray-500" data-id="element-226" />;
    }
  };
  return <div className="space-y-6" data-id="element-227">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-228">
        <div className="flex justify-between items-center mb-6" data-id="element-229">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-230">Payment Batches</h3>
          <div className="relative" data-id="element-231">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-232">
              <SearchIcon size={16} className="text-gray-400" data-id="element-233" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search batches..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-234" />
          </div>
        </div>
        <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-235">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-236">
            <thead className="bg-gray-50" data-id="element-237">
              <tr data-id="element-238">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-239">
                  <div className="flex items-center" data-id="element-240">
                    <span data-id="element-241">Batch Name</span>
                    {sortField === 'name' && <ArrowUpDownIcon size={14} className={`ml-1 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 transform rotate-180'}`} data-id="element-242" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdAt')} data-id="element-243">
                  <div className="flex items-center" data-id="element-244">
                    <span data-id="element-245">Created</span>
                    {sortField === 'createdAt' && <ArrowUpDownIcon size={14} className={`ml-1 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 transform rotate-180'}`} data-id="element-246" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('totalAmount')} data-id="element-247">
                  <div className="flex items-center" data-id="element-248">
                    <span data-id="element-249">Total Amount</span>
                    {sortField === 'totalAmount' && <ArrowUpDownIcon size={14} className={`ml-1 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 transform rotate-180'}`} data-id="element-250" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-251">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('collectionRate')} data-id="element-252">
                  <div className="flex items-center" data-id="element-253">
                    <span data-id="element-254">Collection</span>
                    {sortField === 'collectionRate' && <ArrowUpDownIcon size={14} className={`ml-1 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 transform rotate-180'}`} data-id="element-255" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-256">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-257">
              {filteredBatches.map(batch => <tr key={batch.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewDetail(batch.id)} data-id="element-258">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-259">
                    <div className="text-sm font-medium text-gray-900" data-id="element-260">
                      {batch.name}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-261">
                      {batch.totalItems} requests
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-262">
                    {formatDate(batch.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-263">
                    <div className="text-sm font-medium text-gray-900" data-id="element-264">
                      {batch.currency} {batch.totalAmount.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-265">
                    <div className="flex items-center" data-id="element-266">
                      {getStatusIcon(batch.status)}
                      <span className="ml-1.5 text-sm text-gray-700 capitalize" data-id="element-267">
                        {batch.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-268">
                    <div className="flex items-center" data-id="element-269">
                      <div className="w-full bg-gray-200 rounded-full h-2 mr-2 max-w-[100px]" data-id="element-270">
                        <div className={`h-2 rounded-full ${batch.collectionRate >= 80 ? 'bg-green-500' : batch.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${batch.collectionRate}%`
                    }} data-id="element-271"></div>
                      </div>
                      <span className="text-sm text-gray-700" data-id="element-272">
                        {batch.collectionRate}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-273">
                      {batch.completedItems} of {batch.totalItems} paid
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-274">
                    <button className="text-blue-600 hover:text-blue-900" onClick={e => {
                  e.stopPropagation();
                  onViewDetail(batch.id);
                }} data-id="element-275">
                      <ChevronRightIcon size={18} data-id="element-276" />
                    </button>
                  </td>
                </tr>)}
              {filteredBatches.length === 0 && <tr data-id="element-277">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-278">
                    No batches found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}